import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Stack,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import LinkIcon from '@mui/icons-material/Link'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import MovieIcon from '@mui/icons-material/Movie'
import TvIcon from '@mui/icons-material/Tv'

type OutputMode = 'library' | 'off' | 'playlist'

interface OutputFormatConfig {
  embyPlaylistsSupported: boolean
  moviesOutputMode: OutputMode
  seriesOutputMode: OutputMode
  moviesUseSymlinks: boolean
  seriesUseSymlinks: boolean
}

export function OutputFormatSection() {
  const { t } = useTranslation()
  const [config, setConfig] = useState<OutputFormatConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/settings/ai-recs/output')
      if (!response.ok) throw new Error(t('settingsOutputFormat.fetchFailed'))
      const data = await response.json()
      setConfig(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settingsOutputFormat.unknownError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void fetchConfig()
  }, [fetchConfig])

  const handleSave = async () => {
    if (!config) return
    try {
      setSaving(true)
      setError(null)
      const response = await fetch('/api/settings/ai-recs/output', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (!response.ok) throw new Error(t('settingsOutputFormat.saveFailed'))
      setSuccess(t('settingsOutputFormat.saved'))
      setHasChanges(false)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settingsOutputFormat.unknownError'))
    } finally {
      setSaving(false)
    }
  }

  const updateConfig = (updates: Partial<OutputFormatConfig>) => {
    if (!config) return
    setConfig({ ...config, ...updates })
    setHasChanges(true)
  }

  if (loading) {
    return (
      <Card sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (!config) {
    return (
      <Card sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
        <CardContent>
          <Alert severity="error">{t('settingsOutputFormat.loadFailed')}</Alert>
        </CardContent>
      </Card>
    )
  }

  const anyUseSymlinks = config.moviesUseSymlinks || config.seriesUseSymlinks

  return (
    <Card sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
      <CardContent>
        <Box mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderOpenIcon color="primary" /> {t('settingsOutputFormat.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settingsOutputFormat.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Movies and Series Output Format */}
        <Grid container spacing={3}>
          {/* Movies Library */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MovieIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="medium">
                  {t('settingsOutputFormat.moviesLibrary')}
                </Typography>
              </Box>
              <TextField
                select
                fullWidth
                size="small"
                label={t('settingsOutputFormat.publishMode')}
                value={config.moviesOutputMode}
                onChange={(event) => updateConfig({ moviesOutputMode: event.target.value as OutputMode })}
                sx={{ mb: 1 }}
              >
                <MenuItem value="library">{t('settingsOutputFormat.publishLibrary')}</MenuItem>
                {config.embyPlaylistsSupported && (
                  <MenuItem value="playlist">{t('settingsOutputFormat.publishPlaylist')}</MenuItem>
                )}
                <MenuItem value="off">{t('settingsOutputFormat.publishOff')}</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    disabled={config.moviesOutputMode !== 'library'}
                    checked={config.moviesUseSymlinks}
                    onChange={(e) => updateConfig({ moviesUseSymlinks: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.moviesUseSymlinks ? <LinkIcon fontSize="small" /> : <FolderOpenIcon fontSize="small" />}
                    <Typography variant="body2">
                      {config.moviesUseSymlinks
                        ? t('settingsOutputFormat.modeSymlinks')
                        : t('settingsOutputFormat.modeStrm')}
                    </Typography>
                  </Box>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {config.moviesUseSymlinks
                  ? t('settingsOutputFormat.moviesSymlinkHelp')
                  : t('settingsOutputFormat.moviesStrmHelp')}
              </Typography>
            </Card>
          </Grid>

          {/* Series Library */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TvIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="medium">
                  {t('settingsOutputFormat.seriesLibrary')}
                </Typography>
              </Box>
              <TextField
                select
                fullWidth
                size="small"
                label={t('settingsOutputFormat.publishMode')}
                value={config.seriesOutputMode}
                onChange={(event) => updateConfig({ seriesOutputMode: event.target.value as OutputMode })}
                sx={{ mb: 1 }}
              >
                <MenuItem value="library">{t('settingsOutputFormat.publishLibrary')}</MenuItem>
                {config.embyPlaylistsSupported && (
                  <MenuItem value="playlist">{t('settingsOutputFormat.publishPlaylist')}</MenuItem>
                )}
                <MenuItem value="off">{t('settingsOutputFormat.publishOff')}</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    disabled={config.seriesOutputMode !== 'library'}
                    checked={config.seriesUseSymlinks}
                    onChange={(e) => updateConfig({ seriesUseSymlinks: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.seriesUseSymlinks ? <LinkIcon fontSize="small" /> : <FolderOpenIcon fontSize="small" />}
                    <Typography variant="body2">
                      {config.seriesUseSymlinks
                        ? t('settingsOutputFormat.modeSymlinks')
                        : t('settingsOutputFormat.modeStrm')}
                    </Typography>
                  </Box>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {config.seriesUseSymlinks
                  ? t('settingsOutputFormat.seriesSymlinkHelp')
                  : t('settingsOutputFormat.seriesStrmHelp')}
              </Typography>
              {!config.seriesUseSymlinks && (
                <Alert severity="info" sx={{ mt: 2, py: 0.5 }} icon={false}>
                  <Typography variant="caption">
                    {t('settingsOutputFormat.seriesStrmRecommendation')}
                  </Typography>
                </Alert>
              )}
            </Card>
          </Grid>
        </Grid>

        {(config.moviesOutputMode === 'playlist' || config.seriesOutputMode === 'playlist') && (
          <Alert severity="info" sx={{ mt: 2 }}>{t('settingsOutputFormat.publishPlaylistHelp')}</Alert>
        )}

        {/* Explanation Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          <Card variant="outlined" sx={{ p: 2, bgcolor: anyUseSymlinks ? 'action.selected' : 'transparent' }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon fontSize="small" color={anyUseSymlinks ? 'primary' : 'inherit'} />
              {t('settingsOutputFormat.explainSymlinksTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('settingsOutputFormat.explainSymlinksIntro')}
              <br />
              <strong>{t('settingsOutputFormat.requiresLabel')}</strong>{' '}
              {t('settingsOutputFormat.explainSymlinksRequires')}
            </Typography>
          </Card>
          
          <Card variant="outlined" sx={{ p: 2, bgcolor: !anyUseSymlinks ? 'action.selected' : 'transparent' }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOpenIcon fontSize="small" color={!anyUseSymlinks ? 'primary' : 'inherit'} />
              {t('settingsOutputFormat.explainStrmTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('settingsOutputFormat.explainStrmIntro')}
              <br />
              <strong>{t('settingsOutputFormat.bestForLabel')}</strong>{' '}
              {t('settingsOutputFormat.explainStrmBestFor')}
            </Typography>
          </Card>
        </Box>

        {anyUseSymlinks && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('settingsOutputFormat.symlinkWarningTitle')}</strong>{' '}
              {t('settingsOutputFormat.symlinkWarningBody')}
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? t('settingsOutputFormat.saving') : t('settingsOutputFormat.saveChanges')}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}
